import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";
import PageHeader1 from "../../components/common/PageHeader1";
import { handleUnauthorized } from "../../TokenAuth/auth";
import { formatUTCDateForDataGrid, formatUTCDateForExport } from "../../utils/dateUtils";
import { useSearchParams } from "react-router-dom";
import { getIntParam, getStringParam, setIfTruthy } from "../../utils/urlState";

const CouponReportDemo = () => {
  const token = localStorage.getItem("astroVani-admin-token");
  const [searchParams, setSearchParams] = useSearchParams();

  const getCouponValueType = (coupon) => {
    const category = String(coupon?.category || "").toLowerCase();
    if (String(coupon?.discountType || "").toLowerCase().includes("percentage")) return "Percentage";
    if (String(coupon?.discountType || "").toLowerCase().includes("percent")) return "Percentage";
    if (category.includes("percentage")) return "Percentage";
    return "Flat";
  };

  const getCouponAmountLabel = (coupon) => {
    const value = coupon?.discountValue ?? coupon?.amount ?? coupon?.discount ?? null;
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return getCouponValueType(coupon) === "Percentage" ? `${num}%` : `₹${num}`;
  };

  const getPackAmount = (coupon) => {
    const value =
      coupon?.packAmount ??
      coupon?.minActualAmount ??
      coupon?.selectedPackActualAmount ??
      coupon?.selectedPack?.actualAmount ??
      coupon?.selectedPack?.amount ??
      null;
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    return Number.isFinite(num) ? `₹${num}` : String(value);
  };

  const getOfferPopupName = (entity) => {
    return (
      entity?.offerPopupName ||
      entity?.offerPopupTitle ||
      entity?.offerTitle ||
      entity?.offerPopup?.offerTitle ||
      entity?.offerPopup?.title ||
      "-"
    );
  };

  const [coupons, setCoupons] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: getIntParam(searchParams, "page", 0),
    pageSize: getIntParam(searchParams, "pageSize", 10),
  });
  const [totalCoupons, setTotalCoupons] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Filter States
  const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const normalizeSearchType = () => "couponCode";
  const [searchType, setSearchType] = useState(normalizeSearchType(getStringParam(searchParams, "searchType", "couponCode")));
  const [appliedSearchType, setAppliedSearchType] = useState(normalizeSearchType(getStringParam(searchParams, "searchType", "couponCode")));
  const [categoryFilter, setCategoryFilter] = useState(getStringParam(searchParams, "category", ""));
  const [appliedCategoryFilter, setAppliedCategoryFilter] = useState(getStringParam(searchParams, "category", ""));
  const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", ""));
  const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", ""));
  const [appliedStartDate, setAppliedStartDate] = useState(getStringParam(searchParams, "startDate", ""));
  const [appliedEndDate, setAppliedEndDate] = useState(getStringParam(searchParams, "endDate", ""));
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const applySawLoadingRef = useRef(false);
  const isInitialMount = useRef(true);
  const lastParamsRef = useRef(null);
  const [lastAction, setLastAction] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const didInitUrlSyncRef = React.useRef(false);
  const [selectCoupon, setSelectCoupon] = useState({});
  const [openUserModel, setOpenUserModel] = useState(false);
  const modelCloser = () => setOpenUserModel(false);
  const modelOpener = (coupon) => {
    setSelectCoupon(coupon);
    setOpenUserModel(true);
  };


  // Delete dialog states
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Fetch coupons with pagination and filters
  const fetchCoupons = async (page, limit) => {
    if (!token) {
      setError("Authorization token missing");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const apiPage = page + 1;
      let url = `${process.env.REACT_APP_BASEURL}/admin/coupon/report?page=${apiPage}&limit=${limit}`;
      const params = new URLSearchParams();
      if (appliedStartDate) params.append('startDate', appliedStartDate);
      if (appliedEndDate) params.append('endDate', appliedEndDate);
      if (appliedCategoryFilter) params.append('category', appliedCategoryFilter);
      if (appliedSearchQuery && appliedSearchQuery.trim()) params.append(appliedSearchType, appliedSearchQuery.trim());
      if (params.toString()) {
        url += `&${params.toString()}`;
      }
      const res = await fetch(url, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch coupons");
      const data = await res.json();
      const mappedRows = (data?.data || [])
        .filter((item) => item?.type?.toLowerCase() !== "education")
        .map((item, index) => ({
          ...item,
          id: item._id || item.id || index,
          couponName: item?.couponName || item?.name || "-",
          couponCode: item?.couponCode,
          category: item?.category,
          amount: getCouponAmountLabel(item),
          couponValueType: getCouponValueType(item),
          packAmount: getPackAmount(item),
          offerPopupName: getOfferPopupName(item),
          usedCoupons: item?.usedCoupons || item?.usedBy?.length || "0",
          validFrom: item.validFrom,
          validTo: item.validTo,
          createdAt: item?.createdAt,
          sono: (paginationModel.page * paginationModel.pageSize) + index + 1,
        }));
      setCoupons(mappedRows);
      setTotalCoupons(data?.paginationDetail?.totalDocs || 0);
    } catch (err) {
      setError(err.message || "Unknown error");
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  // Apply Filters Handler
  const handleApplyFilters = () => {
    setIsApplyingFilters(true);
    applySawLoadingRef.current = false;
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedSearchType(searchType);
    setAppliedCategoryFilter(categoryFilter);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setLastAction("apply");
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };


  useEffect(() => {
    const { page, pageSize } = paginationModel;
    let limit = Number(pageSize);
    if (isNaN(limit)) {
      limit = totalCoupons || 10;
    }
    fetchCoupons(page ?? 0, limit);
  }, [paginationModel, appliedStartDate, appliedEndDate, appliedCategoryFilter, appliedSearchQuery, appliedSearchType, lastAction]);

  // Apply button should stay disabled until request completes.
  useEffect(() => {
    if (!isApplyingFilters) return;
    if (loading) {
      applySawLoadingRef.current = true;
      return;
    }
    if (!loading && applySawLoadingRef.current) {
      applySawLoadingRef.current = false;
      setIsApplyingFilters(false);
    }
  }, [loading, isApplyingFilters]);

  // Keep URL in sync so Back restores pagination/filters.
  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }
    const params = new URLSearchParams();
    params.set("page", String(paginationModel.page));
    params.set("pageSize", String(paginationModel.pageSize));
    if (appliedSearchQuery && appliedSearchQuery.trim()) {
      params.set("searchType", appliedSearchType);
      params.set("search", appliedSearchQuery.trim());
    }
    setIfTruthy(params, "category", appliedCategoryFilter);
    setIfTruthy(params, "startDate", appliedStartDate);
    setIfTruthy(params, "endDate", appliedEndDate);
    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, appliedSearchQuery, appliedSearchType, appliedCategoryFilter, appliedStartDate, appliedEndDate, setSearchParams]);


  // Open delete dialog
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  // Close delete dialog
  const handleClose = () => setOpen(false);

  // Dummy delete handler: locally remove item
  const handleDelete = () => {
    setCoupons((prev) => prev.filter((c) => c.id !== selectedId));
    setOpen(false);
  };

  // Pagination Handlers
  const handleChangePage = (event, newPage) =>
    setPaginationModel((prev) => ({ ...prev, page: newPage }));

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    const newSize = parseInt(value, 10);
    setPaginationModel({ page: 0, pageSize: newSize });
  };

  // Reset Filter Handler
  const isFilterActive = startDate || endDate || searchQuery || categoryFilter;

  const handleResetFilter = () => {
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setCategoryFilter("");
    setAppliedCategoryFilter("");
    setSearchQuery("");
    setAppliedSearchQuery("");
    setSearchType("couponCode");
    setAppliedSearchType("couponCode");
    setLastAction(null);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  // Filter and transform rows with useMemo
  const rows = useMemo(() => {
    const pageSize = paginationModel.pageSize;
    return coupons.map((item, index) => ({
      ...item,
      sono: index + 1 + paginationModel.page * pageSize,
    }));
  }, [coupons, paginationModel]);

  // Export to Excel function - fetches all data from API
  // const exportToExcel = async () => {
  //   setIsExporting(true);
  //   try {
  //     if (!token) {
  //       setError("Authorization token missing");
  //       setIsExporting(false);
  //       return;
  //     }

  //     // Fetch all data from API with current filters
  //     let url = `${process.env.REACT_APP_BASEURL}/admin/coupon/report`;

  //     const params = new URLSearchParams();
  //     if (startDate) params.append('startDate', startDate);
  //     if (endDate) params.append('endDate', endDate);
  //     if (searchQuery) params.append('search', searchQuery);

  //     if (params.toString()) {
  //       url += `?${params.toString()}`;
  //     }

  //     const response = await axios.get(url, {
  //       headers: {
  //         Authorization: token,
  //       },
  //     });

  //     if (response.status === 401) {
  //       handleUnauthorized();
  //       throw new Error("Unauthorized: Please login again");
  //     }

  //     const allData = (response.data?.data || [])
  //       .filter((item) => item?.type?.toLowerCase() !== "education");

  //     if (!Array.isArray(allData) || allData.length === 0) {
  //       setError("No data available to export");
  //       setIsExporting(false);
  //       return;
  //     }
  //     console.log(allData,"111111111111111111");

  //     const exportData = allData.map((row, index) => ({
  //       "S.No": index + 1,
  //       "Category": row.category || "",
  //       // "Type": row.type || "",
  //       // "Code": row.code || "-",
  //       // "Google Coupon Code": row.type === "google_pay"
  //       //   ? row.uniqueCodes?.map((uc) => uc.code).join(", ")
  //       //   : row.code || "",
  //       "Coupon Code": row.couponCode || "-",
  //       // "Max Usage Limit": row.noOfUsersUse || row.numCoupons || "N/A",
  //       "Used Coupons": row.usedCoupons || row.usedBy?.length || "0",
  //       "Valid From": formatUTCDateForExport(row.validFrom),
  //       "Valid To": formatUTCDateForExport(row.validTo),
  //       "Created At": formatUTCDateForExport(row.createdAt),
  //       // "Updated At": formatUTCDateForExport(row.updatedAt),
  //     }));

  //     const worksheet = XLSX.utils.json_to_sheet(exportData);
  //     const workbook = XLSX.utils.book_new();
  //     XLSX.utils.book_append_sheet(workbook, worksheet, "CouponReport");
  //     XLSX.writeFile(workbook, `Coupon_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  //   } catch (error) {
  //     console.error("Export error:", error);
  //     setError(error.response?.data?.message || "Failed to export data");
  //   } finally {
  //     setIsExporting(false);
  //   }
  // };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      if (!token) {
        setError("Authorization token missing");
        return;
      }

      // Export should match the list filters (applied* states), not the "typing" states.
      const buildReportUrl = () => {
        const params = new URLSearchParams();
        setIfTruthy(params, "startDate", appliedStartDate);
        setIfTruthy(params, "endDate", appliedEndDate);
        setIfTruthy(params, "category", appliedCategoryFilter);
        if (appliedSearchQuery && appliedSearchQuery.trim()) {
          params.set(appliedSearchType || "couponCode", appliedSearchQuery.trim());
        }
        return `${process.env.REACT_APP_BASEURL}/admin/coupon/report?${params.toString()}`;
      };

      const url = buildReportUrl();
      const response = await axios.get(url, { headers: { Authorization: token } });

      if (response.status === 401) {
        handleUnauthorized();
        throw new Error("Unauthorized: Please login again");
      }

      const allData = (response.data?.data || [])
        .filter((item) => item?.type?.toLowerCase() !== "education");

      if (!Array.isArray(allData) || allData.length === 0) {
        setError("No data available to export");
        return;
      }

      // --- Data Transformation Start ---
      const exportData = [];
      let serialNumber = 1;

      allData.forEach((coupon) => {
        const users = coupon.usedByUsers || [];
        const amountLabel = getCouponAmountLabel(coupon);
        const valueType = getCouponValueType(coupon);
        const packAmount = getPackAmount(coupon);
        const couponLevelOfferPopupName = getOfferPopupName(coupon);

        if (users.length > 0) {
          // Agar coupon use hua hai, to har user ke liye ek row banegi
          users.forEach((user) => {
            exportData.push({
              "S.No": serialNumber++,
              "User Name": user.userName || "Deleted user", // Requirement: User na ho to 'Deleted user'
              "Mobile Number": user.mobileNumber || "-",
              "Platform": user.platform || "-",
              "OS Version": user.osVersion || "-",
              "Used From Offer Popup": user.isUsedFromOfferPopup ? "Yes" : "No",
              "Offer Popup Name": getOfferPopupName(user) !== "-" ? getOfferPopupName(user) : couponLevelOfferPopupName,
              "Coupon Name": coupon.couponName || "-",
              "Category": coupon.category || "",
              "Coupon Code": coupon.couponCode || "-",
              "Amount": amountLabel,
              "Type": valueType,
              "Pack Amount": packAmount,
              "City": user.city || "-",
              "State": user.state || "-",
              "Valid From": formatUTCDateForExport(coupon.validFrom),
              "Valid To": formatUTCDateForExport(coupon.validTo),
              "Used At": formatUTCDateForExport(user.usedAt),
              "Created At": formatUTCDateForExport(coupon.createdAt),
            });
          });
        } else {
          // Agar aap chahte hain ki wo coupons bhi dikhen jo kisi ne use nahi kiye, 
          // to aap ye block rakh sakte hain, warna ise hata sakte hain.

          exportData.push({
            "S.No": serialNumber++,
            "User Name": "Not Used Yet",
            "Mobile Number": "-",
            "Platform": "-",
            "OS Version": "-",
            "Used From Offer Popup": "-",
            "Offer Popup Name": couponLevelOfferPopupName,
            "Coupon Name": coupon.couponName || "-",
            "Category": coupon.category || "",
            "Coupon Code": coupon.couponCode || "-",
            "Amount": amountLabel,
            "Type": valueType,
            "Pack Amount": packAmount,
            "City": "-",
            "State": "-",
            "Valid From": formatUTCDateForExport(coupon.validFrom),
            "Valid To": formatUTCDateForExport(coupon.validTo),
            "Used At": "-",
            "Created At": formatUTCDateForExport(coupon.createdAt),
          });
        }
      });
      // --- Data Transformation End ---

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "CouponUsageReport");
      XLSX.writeFile(workbook, `Coupon_Report_${new Date().toISOString().split('T')[0]}.xlsx`);

    } catch (error) {
      console.error("Export error:", error);
      setError(error.response?.data?.message || "Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    {
      field: "sono",
      headerName: "S.No",
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: "action", headerName: "Action", width: 70, renderCell: (params) => (
        <IconButton size="small" component={"span"} onClick={() => modelOpener(params.row)} sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}>
          <i className="icofont-eye" style={{ fontSize: '18px' }}></i>
        </IconButton>
      )
    },
    { field: "category", headerName: "Coupon Category", width: 160 },
    { field: "couponName", headerName: "Coupon Name", width: 180 },
    // { field: "type", headerName: "Coupon Type", width: 150 },
    // { field: "uniqueCodes", headerName: "Google Coupon Code", width: 180 },
    { field: "couponCode", headerName: "Coupon Code", width: 150 },
    { field: "amount", headerName: "Amount", width: 120 },
    { field: "couponValueType", headerName: "Type", width: 120 },
    { field: "packAmount", headerName: "Pack Amount", width: 140 },
    { field: "offerPopupName", headerName: "Offer Popup Name", width: 180 },
    // {
    //   field: "noOfUsersUse",
    //   headerName: "Max Usage Limit",
    //   width: 160,
    //   headerAlign: 'center',
    //   align: 'center',
    // },
    {
      field: "usedCoupons",
      headerName: "Used Coupons",
      width: 140,
      headerAlign: 'center',
      align: 'center',
    },
    { field: "validFrom", headerName: "Valid From", width: 180, valueFormatter: formatUTCDateForDataGrid },
    { field: "validTo", headerName: "Valid To", width: 180, valueFormatter: formatUTCDateForDataGrid },
    { field: "createdAt", headerName: "Created At", width: 180, valueFormatter: formatUTCDateForDataGrid },
    // { field: "updatedAt", headerName: "Updated At", width: 180, valueFormatter: formatUTCDateForDataGrid },
  ];

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Coupon Usage Report" />

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: '8px',
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Search, Filter, and Export Toolbar */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="flat_extra_percentage">flat_extra_percentage</MenuItem>
              <MenuItem value="flat_extra_rupees">flat_extra_rupees</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Search By</InputLabel>
            <Select value={searchType} label="Search By" onChange={(e) => setSearchType(e.target.value)}>
              <MenuItem value="couponCode">Coupon Code</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder="Search..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
            sx={{ width: 200 }}
          />
          <TextField label="Start Date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="End Date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={handleApplyFilters} disabled={loading || isApplyingFilters}>
            {isApplyingFilters ? "Applying..." : "Apply Filters"}
          </Button>
          <Button variant="outlined" onClick={handleResetFilter}>Reset</Button>
          <Button variant="contained" color="success" onClick={exportToExcel} disabled={isExporting}>
            {isExporting ? "Exporting..." : "Export to Excel"}
          </Button>
        </Box>

        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card" style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: 'none',
              overflow: 'hidden'
            }}>
              <div className="card-body" style={{ padding: 0 }}>
                {/* Custom Pagination on Top */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <TablePagination
                    component="div"
                    count={totalCoupons || 0}
                    page={paginationModel.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={paginationModel.pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </Box>

                <Box sx={{
                  width: '100%',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef',
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                }}>
                  <DataGrid
                    autoHeight
                    columns={columns}
                    rows={rows}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50, 100]}
                    loading={loading}
                    disableSelectionOnClick
                    getRowId={(row) => row.id}
                    hideFooterPagination
                    hideFooter
                    sx={{
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                    }}
                  />
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* For Delete Coupon */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this coupon?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* For View Users */}
      {/* <Dialog open={openUserModel} onClose={modelCloser} maxWidth="md" fullWidth className="custom-dialog ms-md-5">
        <DialogTitle>View Users for Coupon</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectCoupon.couponCode} - {selectCoupon.category}
          </DialogContentText>
        </DialogContent>
        <DialogContent>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>User Name</th>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>City</th>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>State</th>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>Mobile Number</th>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>OS Version</th>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>Platform</th>
                <th style={{ border: '1px solid #ddd', padding: '8px' }}>Used At</th>
              </tr>
            </thead>
            <tbody>
              {selectCoupon.usedByUsers?.length > 0 ? (
                selectCoupon.usedByUsers.map((user, index) => (
                  <tr key={index}>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{user.userName}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{user.city}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{user.state}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{user.mobileNumber}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{user.osVersion}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{user.platform}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{formatUTCDateForDataGrid(user.usedAt)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                    No users have used this coupon yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </DialogContent>
        <DialogActions>
          <Button onClick={modelCloser}>Close</Button>
        </DialogActions>
      </Dialog> */}

      <Dialog
        open={openUserModel}
        onClose={modelCloser}
        maxWidth="md"
        fullWidth
      // sx={{
      //   ml: { xs: 0, md: 30 },
      // }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}>
          <Box style={{ maxHeight: 40, overflowY: 'auto' }}>
            <span style={{ fontWeight: 'bold' }}>{selectCoupon?.couponName}</span> (
            {selectCoupon?.couponCode} - {selectCoupon?.category})
          </Box>
          <Chip
            label={selectCoupon?.isActive ? "Coupon Status: Active" : "Coupon Status: Inactive"}
            color={selectCoupon?.isActive ? "success" : "error"}
            size="small"
            variant="outlined"
          />
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, p: 0, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Box sx={{ minWidth: '150px', fontSize: 14 }}><strong>Coupon Name:</strong> {selectCoupon?.couponName || "-"}</Box>
            <Box sx={{ minWidth: '120px', fontSize: 14 }}><strong>Coupon Code:</strong> {selectCoupon?.couponCode || "-"}</Box>
            <Box sx={{ minWidth: '100px', fontSize: 14 }}><strong>Amount:</strong> {selectCoupon?.amount || "-"}</Box>
            <Box sx={{ minWidth: '100px', fontSize: 14 }}><strong>Type:</strong> {selectCoupon?.couponValueType || "-"}</Box>
            <Box sx={{ minWidth: '150px', fontSize: 14 }}><strong>Pack Amount:</strong> {selectCoupon?.packAmount || "-"}</Box>
          </Box>
          <DialogContentText
            sx={{
              mb: 1,
              fontSize: 16,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {/* Left Side */}
              <Box sx={{ fontStyle: 'italic', fontSize: '14px', color: 'text.secondary', maxHeight: 40, overflowY: 'auto' }}>
                {selectCoupon?.description}
              </Box>

              {/* Right Side */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
                Total Users: {selectCoupon?.usedByUsers?.length || 0}
              </Box>
            </Box>
          </DialogContentText>
          <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>User Name</strong></TableCell>
                  <TableCell><strong>City</strong></TableCell>
                  <TableCell><strong>State</strong></TableCell>
                  <TableCell><strong>Mobile Number</strong></TableCell>
                  <TableCell><strong>OS Version</strong></TableCell>
                  <TableCell><strong>Used From Offer Popup</strong></TableCell>
                  <TableCell><strong>Offer Popup Name</strong></TableCell>
                  <TableCell><strong>Platform</strong></TableCell>
                  <TableCell><strong>Used At</strong></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {selectCoupon?.usedByUsers?.length > 0 ? (
                  selectCoupon.usedByUsers.map((user, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{user.userName || "-"}</TableCell>
                      <TableCell>{user.city || "-"}</TableCell>
                      <TableCell>{user.state || "-"}</TableCell>
                      <TableCell>{user.mobileNumber || "-"}</TableCell>
                      <TableCell>{user.osVersion || "-"}</TableCell>
                      <TableCell>
                        {user.isUsedFromOfferPopup ? "Yes" : "No"}
                      </TableCell>
                      <TableCell>{getOfferPopupName(user)}</TableCell>
                      <TableCell>{user.platform || "-"}</TableCell>
                      <TableCell>
                        {formatUTCDateForDataGrid(user.usedAt)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No users have used this coupon yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={modelCloser} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default CouponReportDemo;
